import { cents, roundHalfUpToCent } from '../money/index.js';
import { failure, success } from '../types.js';
import type { AwardTypeCode, Cents, Fraction, Pool, Result } from '../types.js';
import { amountIssue, countIssue, fractionIssue } from '../validation.js';
import { solveAssignment } from './solver.js';
import type { AssignmentLimits } from './solver.js';
export type { AssignmentLimits } from './solver.js';

/**
 * Where a Spot Award is paid from, as the `spot.fundingPool` setting says (K37): the Spot Awards
 * pool, the one pool of its own (APP-264, Tom 2026-11-01, the default); the nominee's sector pool
 * (K37's default before it); or one named pool.
 */
export type SpotFunding =
  | { readonly kind: 'spot_pool' }
  | { readonly kind: 'nominee_sector' }
  | { readonly kind: 'fixed'; readonly poolId: string };
export const DEFAULT_SPOT_FUNDING: SpotFunding = { kind: 'spot_pool' };

export interface PoolSelection {
  readonly awardType: AwardTypeCode;
  readonly sectorId: string | null;
  readonly pools: readonly Pool[];
  readonly nomineeSectorId?: string | null;
  /** A Spot Award's rule; the Spot Awards pool when not given. */
  readonly spotFunding?: SpotFunding;
}
export function poolForAward(input: PoolSelection): Result<string> {
  if (input.awardType === 'TAA') return failure('taa_assignment_required');
  const selected = input.pools.filter((pool) => {
    switch (input.awardType) {
      case 'PAA':
        return pool.kind === 'paa_taa';
      case 'EDB':
        return pool.kind === 'executive';
      // K37, APP-264: the configured Spot rule; by default the Spot Awards pool.
      case 'SPOT': {
        const rule = input.spotFunding ?? DEFAULT_SPOT_FUNDING;
        return rule.kind === 'fixed'
          ? pool.id === rule.poolId
          : rule.kind === 'spot_pool'
            ? pool.kind === 'spot'
            : pool.kind === 'sector' &&
              input.nomineeSectorId != null &&
              pool.sectorId === input.nomineeSectorId;
      }
      case 'SDB':
        return (
          pool.kind === 'sector' && input.sectorId !== null && pool.sectorId === input.sectorId
        );
      default:
        return false;
    }
  });
  return selected.length === 1 && selected[0]
    ? success(selected[0].id)
    : failure(selected.length > 1 ? 'ambiguous_funding_pool' : 'missing_funding_pool');
}

export interface FundedAmount {
  readonly poolId: string;
  readonly cashCents: number;
  readonly options: number;
}
export interface PoolTotal {
  readonly poolId: string;
  readonly cashCents: Cents;
  readonly options: number;
}
export function paidFrom(lines: readonly FundedAmount[]): Result<readonly PoolTotal[]> {
  const totals = new Map<string, { cash: bigint; options: bigint }>();
  for (const line of lines) {
    if (!line.poolId.trim()) return failure('missing_funding_pool');
    const issues = [
      ...amountIssue(line.cashCents, 'cashCents'),
      ...countIssue(line.options, 'options'),
    ];
    if (issues.length) return { ok: false, issues };
    const old = totals.get(line.poolId) ?? { cash: 0n, options: 0n };
    totals.set(line.poolId, {
      cash: old.cash + BigInt(line.cashCents),
      options: old.options + BigInt(line.options),
    });
  }
  const output: PoolTotal[] = [];
  for (const [poolId, total] of totals) {
    if (
      total.cash > BigInt(Number.MAX_SAFE_INTEGER) ||
      total.options > BigInt(Number.MAX_SAFE_INTEGER)
    )
      return failure('amount_overflow');
    output.push({ poolId, cashCents: cents(Number(total.cash)), options: Number(total.options) });
  }
  return success(output);
}

export type TaaPool = 'paa_taa' | 'sector';
export interface TaaLine {
  readonly personId: string;
  readonly cashCents: number;
  readonly pool?: TaaPool;
}
export interface AssignedTaaLine {
  readonly personId: string;
  readonly cashCents: Cents;
  readonly pool: TaaPool;
}
export interface TaaAssignment {
  readonly mode: 'auto' | 'manual';
  readonly assignment: readonly AssignedTaaLine[];
  readonly paaTotalCents: Cents;
  readonly sectorTotalCents: Cents;
  readonly targetCents: Cents;
  readonly differenceCents: Cents;
  readonly exact: boolean;
}

function summarize(
  lines: readonly AssignedTaaLine[],
  mode: 'auto' | 'manual',
  share: Fraction,
  exact: boolean,
): TaaAssignment {
  let paa = 0n;
  let sector = 0n;
  for (const line of lines) {
    if (line.pool === 'paa_taa') paa += BigInt(line.cashCents);
    else sector += BigInt(line.cashCents);
  }
  const targetCents = roundHalfUpToCent(Number(paa + sector), share);
  return {
    mode,
    assignment: lines,
    paaTotalCents: cents(Number(paa)),
    sectorTotalCents: cents(Number(sector)),
    targetCents,
    differenceCents: cents(Number(paa) - targetCents),
    exact,
  };
}

/** Exact dollar-target interpretation of WP-DOMAIN §4; assignments always use whole people. */
export function assignTaaPools(
  lines: readonly TaaLine[],
  paaShare: Fraction,
  mode: 'auto' | 'manual',
  limits: AssignmentLimits = { maxReachableSums: 50_000, maxWorkUnits: 1_000_000 },
): Result<TaaAssignment> {
  const ratioIssues = fractionIssue(paaShare, 'paaShare');
  if (ratioIssues.length) return { ok: false, issues: ratioIssues };
  if (lines.length === 0) return failure('invalid_assignment_size');
  const ids = new Set<string>();
  let total = 0n;
  for (const line of lines) {
    if (!line.personId.trim() || ids.has(line.personId))
      return failure('duplicate_or_missing_recipient');
    ids.add(line.personId);
    const issues = amountIssue(line.cashCents, 'cashCents', true);
    if (issues.length) return { ok: false, issues };
    total += BigInt(line.cashCents);
  }
  if (total > BigInt(Number.MAX_SAFE_INTEGER)) return failure('amount_overflow');
  if (mode === 'manual') {
    const assigned: AssignedTaaLine[] = [];
    for (const line of lines) {
      if (line.pool !== 'paa_taa' && line.pool !== 'sector')
        return failure('manual_pool_assignment_required');
      assigned.push({ personId: line.personId, cashCents: cents(line.cashCents), pool: line.pool });
    }
    return success(summarize(assigned, 'manual', paaShare, false));
  }
  if (
    ![limits.maxReachableSums, limits.maxWorkUnits].every((n) => Number.isSafeInteger(n) && n > 0)
  )
    return failure('invalid_assignment_limits');
  const solution = solveAssignment(
    lines.map((line) => BigInt(line.cashCents)),
    paaShare,
    limits,
  );
  const chosenMask = solution.mask;
  const assigned = lines.map((line, index): AssignedTaaLine => ({
    personId: line.personId,
    cashCents: cents(line.cashCents),
    pool: (chosenMask & (1n << BigInt(lines.length - index - 1))) !== 0n ? 'paa_taa' : 'sector',
  }));
  return success(summarize(assigned, 'auto', paaShare, solution.exact));
}

export function moveTaaPerson(
  lines: readonly TaaLine[],
  personId: string,
  pool: TaaPool,
  paaShare: Fraction,
): Result<TaaAssignment> {
  if (!lines.some((line) => line.personId === personId)) return failure('recipient_not_found');
  return assignTaaPools(
    lines.map((line) => (line.personId === personId ? { ...line, pool } : { ...line })),
    paaShare,
    'manual',
  );
}
